// assets/js/components/revenue-calculator/index.js
//
// Public entry point. Mirrors the spec's App(): owns step/path/adv state,
// re-renders the current step via innerHTML on every mutation (no virtual
// DOM — see quiz-app.js for the established precedent of this technique).
//
// See ./README.md for the full step map, actions contract, and known caveats.

var state = require("./state");
var ui = require("./ui");
var stepEntry = require("./steps/step-entry");
var step1 = require("./steps/step-1");
var step2 = require("./steps/step-2");
var step3 = require("./steps/step-3");
var step4 = require("./steps/step-4");
var step5 = require("./steps/step-5");
var step6a = require("./steps/step-6a");
var step6b = require("./steps/step-6b");
var step6c = require("./steps/step-6c");
var leadGate = require("./steps/lead-gate");
var resultsPage = require("./steps/results-page");
var calc = require("./calc");
var intlData = require("./intl-data");

var LEAD_ENDPOINT = "/api/revenue-calculator/lead";

// Build the lead-capture payload (snake_case keys — the backend maps them onto its DTO).
// Besides the raw answers and computed figures it carries the language-independent
// "report" spec (recommendation / benchmark ids), so the results email is rendered
// server-side from an allow-list and never from client HTML.
// Blank optional answers are left out: the API validates them as numbers when present.
function compactInputs(d) {
    var out = {};
    Object.keys(d).forEach(function (k) {
        if (d[k] !== "" && d[k] != null) out[k] = d[k];
    });
    return out;
}

function buildLeadPayload(s, locale, siteLocale, eff) {
    var d = s.d;
    var sym = intlData.getCurrencySymbol(locale, d.currency);
    var r = calc.calcResults(d, s.path, sym);
    var recs = calc.buildRecs(d, r, s.path).map(function (rec) {
        return { id: rec.id, features: rec.f, hours: rec.hours };
    });
    var results = {};
    ["bm", "ns", "avg", "dur", "hrly", "onlineSt", "nsRaw", "nsLoss", "nsMit", "emRaw", "emLoss", "cancRaw", "cancLoss", "cancMit",
        "missedL", "retL", "total", "nsRec", "schedRec", "retRec", "totalRec", "adminVal", "lossScore", "isWellOpt", "hasToolsLosing"
    ].forEach(function (k) { results[k] = r[k]; });

    return {
        email: s.email,
        first_name: s.firstName,
        smb_usage: eff,
        locale: siteLocale,
        path: s.path,
        results_email_consent: !!s.resultsConsent,
        marketing_email_consent: !!s.marketingConsent,
        inputs: compactInputs(d),
        results: results,
        report: {
            recs: recs,
            benchmarks: calc.getBenchmarkSpec(d, r, s.path),
            no_booking_system: d.hasBookingSystem === "No",
            manual_gcal: d.bookingSystem === "Google Calendar / manual"
        }
    };
}

// The global jQuery (not a require()d copy) on purpose: the layout's $.ajaxSetup adds the CSRF header.
// The results screen never waits for this request, so a failure is logged rather than shown.
function postLead(payload) {
    var jq = window.$ || window.jQuery;
    if (!jq || !jq.ajax) {
        console.error("[RevenueCalculator] lead not sent: jQuery is not available");
        return;
    }
    jq.ajax({ url: LEAD_ENDPOINT, method: "POST", contentType: "application/json", data: JSON.stringify(payload) })
        .fail(function (xhr) {
            console.error("[RevenueCalculator] lead submit failed", xhr.status, xhr.responseText);
        });
}

function resolveStepModule(s) {
    if (s.step === 0) return stepEntry;
    if (s.step === 1) return step1;
    if (s.step === 2) return step2;
    if (s.step === 3) return step3;
    if (s.step === 4) return step4;
    if (s.step === 5) return step5;
    if (s.step === 6 && s.adv === 0) return step6a;
    if (s.step === 6 && s.adv === 1) return step6b;
    if (s.step === 6 && s.adv === 2) return step6c;
    if (s.step === 7) return leadGate;
    return null; // step 8 handled separately (results-page has no bind())
}

function initRevenueCalculator(container) {
    // <html lang> is a BCP 47 tag ("zh-CN", "pt-BR") and drives Intl formatting. The site's own
    // locale code ("cn", "br") comes from the route and goes to the API: slicing the tag to two
    // letters turned "cn" into "zh" (Traditional) and "br" into "pt".
    var locale = document.documentElement.getAttribute("lang") || "en";
    var siteLocale = container.getAttribute("data-locale") || String(locale).slice(0, 2).toLowerCase();
    var s = state.createState();

    var chrome = document.createElement("div");
    chrome.className = "rc-calc__chrome";
    var stepEl = document.createElement("div");
    stepEl.className = "rc-calc__step-container";
    container.appendChild(chrome);
    container.appendChild(stepEl);

    function renderChrome() {
        // Nothing to show progress on before "Start your revenue check" is clicked.
        if (s.step === 0) { chrome.innerHTML = ""; return; }
        var showBack = s.step < 8;
        chrome.innerHTML =
            (showBack ? '<div class="rc-calc__chrome-row">' + ui.backButton() + "</div>" : "") +
            '<p class="p rc-calc__eyebrow-top">' + _t("Revenue Calculator progress") + "</p>" +
            ui.progressBar(state.progressPercent(s));
        var backBtn = chrome.querySelector("[data-rc-back]");
        if (backBtn) backBtn.addEventListener("click", function () { state.goBack(s); rerender(); });
    }

    // Multi-step wizard focus management: a full stepEl.innerHTML swap gives
    // screen-reader users no indication a new step loaded. Moving focus to the
    // new step's own heading (not on the very first render, and not on in-place
    // updates like a country/category change that re-renders the SAME step)
    // both scrolls it into view and gets it announced.
    var lastRenderedStep = s.step, lastRenderedAdv = s.adv;
    function focusStepHeading() {
        var heading = stepEl.querySelector("h1, h2, h3, h4");
        if (!heading) return;
        if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
        heading.focus();
    }

    var actions = {
        setField: function (key, value) { state.setField(s, key, value); },
        toggleArr: function (key, value) { state.toggleArr(s, key, value); },
        rerender: function () { rerender(); },
        showError: function (fieldId, message) {
            // Clear any previously-shown error/aria-invalid in this step before
            // setting the new one, so fixing field A and failing on field B
            // doesn't leave A's stale error and aria-invalid behind.
            stepEl.querySelectorAll("[data-error-for]").forEach(function (slot) { slot.innerHTML = ""; });
            stepEl.querySelectorAll("[aria-invalid]").forEach(function (el) { el.removeAttribute("aria-invalid"); });

            var slot = stepEl.querySelector('[data-error-for="' + fieldId + '"]');
            if (slot) slot.innerHTML = ui.fieldError(message);

            var control = stepEl.querySelector("#" + fieldId);
            if (control) {
                control.setAttribute("aria-invalid", "true");
                if (typeof control.focus === "function") control.focus();
            }
        },
        submitLead: function () {
            var isSMB = s.d.bookingSystem === "SimplyBook.me";
            var eff = isSMB ? "Yes" : s.smbUsage;
            s.smbUsage = eff;
            s.step = 8;
            var payload = buildLeadPayload(s, locale, siteLocale, eff);
            try { sessionStorage.setItem("rc_calculator_lead", JSON.stringify(payload)); } catch (e) {}
            // Fire-and-forget: a slow or failed POST must never block the user from seeing their results.
            try { postLead(payload); } catch (e) {}
            rerender();
        }
    };

    function rerender() {
        renderChrome();
        var stepChanged = s.step !== lastRenderedStep || s.adv !== lastRenderedAdv;
        lastRenderedStep = s.step;
        lastRenderedAdv = s.adv;

        if (s.step >= 8) {
            stepEl.innerHTML = resultsPage.render(s, locale);
            if (stepChanged) focusStepHeading();
            return;
        }
        var mod = resolveStepModule(s);
        stepEl.innerHTML = mod.render(s, locale);
        mod.bind(stepEl, s, actions, locale);
        if (stepChanged) focusStepHeading();
    }

    rerender();
}

module.exports = { initRevenueCalculator: initRevenueCalculator };
